import { FirebaseError } from "@firebase/app";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Form, FormField, FormItem } from "shared/components/ui/form";
import { Input } from "shared/components/ui/input";
import { useToast } from "shared/components/ui/use-toast";
import { AuthService } from "shared/services/AuthService";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "shared/components/ui/loading-button";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "shared/components/ui/alert-dialog";
import colorsStore from "shared/store/colorsStore";

const formSchema = z.object({
  email: z.string().email(),
  password: z.string(),
});

export const EmailLogin: React.FC = () => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });
  const [forgotEmail, setForgotEmail] = useState("");

  const handleError = (error: any) => {
    if (error.name === "FirebaseError") {
      toast({
        title: t("global.error"),
        description: t(`global.firebaseErrors.${error.code}`),
        variant: "destructive",
      });
    } else {
      toast({
        title: t("global.error"),
        description: t("global.unknownError"),
        variant: "destructive",
      });
    }
  };

  const handleLogin = (values: z.infer<typeof formSchema>) => {
    setLoading(true);
    AuthService.signInWithEmailAndPassword(
      values.email as string,
      values.password as string,
    )
      .catch((error: any) => {
        handleError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleForgotPassword = () => {
    AuthService.passwordReset(forgotEmail).then(() => {
      toast({
        title: t("global.success"),
        description: t("login.forgotPasswordSent"),
      });
    });
  };

  return (
    <div className="space-y-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleLogin)}>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("global.email")}
                    required
                    {...field}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <Input
                    id="password"
                    type="password"
                    placeholder={t("global.password")}
                    required
                    {...field}
                  />
                </FormItem>
              )}
            />
            <div className="flex items-center justify-between space-x-4">
              <LoadingButton
                type="submit"
                variant="default"
                loading={loading}
                className="w-full"
              >
                {t("login.login")}
              </LoadingButton>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};
